import React, { FC } from 'react';
import { styled } from '../../styles';
import { BpDesignSystem } from '../../types';
import { CircularProgress } from '../Progress/CircularProgress';
import { FormControl } from '../Form/FormControl';
import { FormLabel } from '../Form/FormLabel';
import { Typography } from '../Typography';
import { TypographyTooltip } from '../TypographyTooltip';

interface OptionValue {
  label: string;
  code?: string;
}

type SelectInactiveProps = BpDesignSystem.Props<{
  disabled?: boolean;
  error?: boolean;
  id?: string;
  label?: React.ReactNode;
  loading?: boolean;
  multiple?: boolean;
  onClick?: () => void;
  placeholder?: string;
  required?: boolean;
  value?: OptionValue | OptionValue[];
}>;

export const SelectInactive: FC<SelectInactiveProps> = ({
  className,
  dataTest = 'select-inactive',
  disabled,
  error,
  id,
  label,
  loading,
  multiple,
  onClick,
  placeholder = 'Select Value...',
  required,
  style,
  sx,
  value
}) => {
  const handleClick = () => !disabled && onClick?.();
  const values = Array.isArray(value) ? value : value ? [value] : [];
  const singleValue = Array.isArray(value) ? value[0] : value;
  const Wrapper = label ? FormControl : React.Fragment;
  const wrapperProps = label ? { fullWidth: true as const } : ({} as { fullWidth: boolean });

  return (
    <Wrapper {...wrapperProps}>
      {!!label && (
        <StyledFormLabel required={required} htmlFor={id} shrink>
          {label}
        </StyledFormLabel>
      )}
      <StyledContainer
        $hasError={error}
        $hasValue={multiple ? values.length > 0 : !!singleValue}
        $isDisabled={disabled}
        $multiple={multiple}
        className={className}
        data-test={dataTest}
        onClick={handleClick}
        onFocus={handleClick}
        role="button"
        style={style}
        sx={sx}
        tabIndex={disabled ? -1 : 0}
      >
        {multiple ? (
          <StyledChipsContent>
            {values.length === 0 ? (
              <StyledPlaceholder variant="body" small>
                {placeholder}
              </StyledPlaceholder>
            ) : (
              values.map((val, i) => (
                <StyledChip key={i}>
                  <TypographyTooltip className="label-item">{val.label}</TypographyTooltip>
                  {val.code && <StyledCode variant="body" small>{`(${val.code})`}</StyledCode>}
                </StyledChip>
              ))
            )}
          </StyledChipsContent>
        ) : (
          <StyledContent>
            {singleValue ? (
              <StyledContentInner>
                <TypographyTooltip className="label-item">{singleValue.label}</TypographyTooltip>
                {singleValue.code && <StyledCode>{`(${singleValue.code})`}</StyledCode>}
              </StyledContentInner>
            ) : (
              <StyledPlaceholder variant="body" small>
                {placeholder}
              </StyledPlaceholder>
            )}
          </StyledContent>
        )}
        {loading && (
          <StyledSpinner>
            <CircularProgress color="inherit" size={20} />
          </StyledSpinner>
        )}
      </StyledContainer>
    </Wrapper>
  );
};

const StyledFormLabel = styled(FormLabel)`
  margin-bottom: 4px;
`;

const StyledContent = styled('div')`
  align-items: center;
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const StyledContentInner = styled('div')`
  align-items: center;
  display: flex;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
  width: 100%;
`;

const StyledCode = styled(Typography)`
  color: ${({ theme }) => theme.tokens.colorOnSurfaceVariant};
  flex-shrink: 0;
`;

const StyledChipsContent = styled('div')`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  gap: 4px 8px;
  min-width: 0;
  padding: 4px 0;
`;

const StyledPlaceholder = styled(Typography)`
  color: ${({ theme }) => theme.tokens.stateOnSurfaceDisabledContentStateLayerColor};
`;

const StyledContainer = styled('div')<{
  $hasError?: boolean;
  $hasValue: boolean;
  $isDisabled?: boolean;
  $multiple?: boolean;
}>`
  align-items: center;
  background: ${({ theme }) => theme.tokens.colorSurface};
  border-radius: ${({ theme }) => theme.tokens.radiusRegular};
  border: 1px solid ${({ $hasError, theme }) => ($hasError ? theme.tokens.colorAlert : theme.tokens.colorSurfaceVariant)};
  box-sizing: border-box;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'default' : 'pointer')};
  display: flex;
  min-height: 36px;
  overflow: hidden;
  padding: ${({ $multiple }) => ($multiple ? '0 8px 0 8px' : '0 10px 0 10px')};
  width: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.tokens.colorOnSurface};
  }

  &:focus {
    border-color: ${({ theme }) => theme.tokens.colorPrimary};
    outline: none;
  }
`;

const StyledSpinner = styled('span')`
  align-items: center;
  align-self: center;
  color: ${({ theme }) => theme.tokens.colorOnSurfaceVariant};
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 0 8px;
`;

const StyledChip = styled('span')`
  align-items: center;
  background-color: ${({ theme }) => theme.tokens.stateOnSurfaceHoverStateLayerColor};
  border-radius: 12px;
  display: inline-flex;
  gap: 4px;
  height: 24px;
  line-height: 1;
  max-width: 100%;
  overflow: hidden;
  padding: 0 8px;
`;
